import React, { useState, useEffect } from 'react';
import { Box, Typography, Card, CardContent, Button, Grid, Chip, Divider, Alert } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../../services/config';

const BillingSettings = () => {
  const navigate = useNavigate();
  const [billingInfo, setBillingInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchBillingInfo();
  }, []);

  const fetchBillingInfo = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/billing/current`, {
        withCredentials: true
      });
      setBillingInfo(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching billing info:', err);
      setError('Impossible de charger les informations de facturation');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getTrialColor = (daysRemaining) => {
    if (daysRemaining === null) return 'default';
    if (daysRemaining <= 3) return 'error';
    if (daysRemaining <= 7) return 'warning';
    return 'success';
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Chargement des informations de facturation...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!billingInfo) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Aucune information de facturation disponible</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Facturation et Abonnement
        </Typography>
        <Typography variant="body2" sx={{ color: '#868e96' }}>
          Gérez votre abonnement et vos informations de facturation
        </Typography>
      </Box>

      {/* Trial Alert */}
      {billingInfo.is_trial && billingInfo.trial_days_remaining !== null && (
        <Alert 
          severity={billingInfo.trial_days_remaining <= 3 ? 'error' : 'warning'} 
          sx={{ mb: 3 }}
        >
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            Période d'essai : {billingInfo.trial_days_remaining} jour{billingInfo.trial_days_remaining > 1 ? 's' : ''} restant{billingInfo.trial_days_remaining > 1 ? 's' : ''}
          </Typography>
          <Typography variant="body2">
            Votre période d'essai expire le {formatDate(billingInfo.subscription_end_date)}. 
            Effectuez une mise à niveau pour continuer à utiliser toutes les fonctionnalités.
          </Typography>
          <Button 
            variant="contained" 
            size="small" 
            sx={{ mt: 1 }}
            onClick={() => navigate('/preferences/upgrade')}
          >
            Voir les plans
          </Button>
        </Alert>
      )}

      {/* Current Subscription Card */}
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
            Abonnement actuel
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" sx={{ color: '#868e96', mb: 0.5, display: 'block' }}>
                  Plan
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {billingInfo.subscription_name || 'No Plan'}
                  </Typography>
                  {billingInfo.is_trial && (
                    <Chip 
                      label="Trial" 
                      size="small" 
                      color={getTrialColor(billingInfo.trial_days_remaining)}
                      sx={{ fontWeight: 600 }}
                    />
                  )}
                </Box>
              </Box>

              {billingInfo.subscription_price !== undefined && billingInfo.subscription_price > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#868e96', mb: 0.5, display: 'block' }}>
                    Price
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                    <MonetizationOnIcon sx={{ fontSize: 20, color: '#10B981' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {billingInfo.subscription_price.toLocaleString('fr-FR')} FCFA
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#868e96' }}>
                      / {billingInfo.billing_cycle === 'annual' ? 'year' : 'month'}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              {billingInfo.subscription_start_date && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#868e96', mb: 0.5, display: 'block' }}>
                    <CalendarTodayIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    Actif depuis
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {formatDate(billingInfo.subscription_start_date)}
                  </Typography>
                </Box>
              )}

              {billingInfo.next_billing_date && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#868e96', mb: 0.5, display: 'block' }}>
                    <AccessTimeIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    Prochaine date de facturation
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {formatDate(billingInfo.next_billing_date)}
                  </Typography>
                </Box>
              )}

              {billingInfo.auto_renew !== undefined && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" sx={{ color: '#868e96', mb: 0.5, display: 'block' }}>
                    <AutorenewIcon sx={{ fontSize: 14, mr: 0.5 }} />
                    Renouvellement automatique
                  </Typography>
                  <Chip 
                    label={billingInfo.auto_renew ? 'Activé' : 'Désactivé'}
                    size="small"
                    color={billingInfo.auto_renew ? 'success' : 'default'}
                    sx={{ fontWeight: 600 }}
                  />
                </Box>
              )}
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button 
              variant="contained" 
              color="primary"
              onClick={() => navigate('/preferences/upgrade')}
            >
              Voir tous les plans
            </Button>
            {billingInfo.is_trial && (
              <Button 
                variant="outlined" 
                color="primary"
                onClick={() => navigate('/preferences/upgrade')}
              >
                Améliorer maintenant
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Invoices Section - Placeholder */}
      <Card sx={{ borderRadius: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700 }}>
            Factures et historique des paiements
          </Typography>
          <Typography variant="body2" sx={{ color: '#868e96', mb: 2 }}>
            Aucune facture disponible pour le moment.
          </Typography>
          <Typography variant="caption" sx={{ color: '#868e96' }}>
            Les factures apparaîtront ici lorsque vous passerez à un plan payant.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BillingSettings;

